import { Component, OnInit, inject } from '@angular/core';
import { ToasterService, TranslateService } from '@devinforius/i-essentials';
import { AuthService } from './core/services/authentication.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { Language } from '@shared/types';

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  private authService: AuthService = inject(AuthService);
  private oAuthService: OAuthService = inject(OAuthService);
  private router: Router = inject(Router);
  private translater: TranslateService = inject(TranslateService);
  private toasterService: ToasterService = inject(ToasterService);
  private isTranslationReady: boolean = false;
  private isUserReady: boolean = false;

  public loading: boolean = true;
  public isConnected: boolean = false;
  // NOTE migration v20 : le type INavbarConf n'est pas exporté par @devinforius/i-essentials.
  // L'objet ci-dessous est construit pour correspondre à sa structure (cf. setINavBarConfig).
  public config: any;

  public async ngOnInit(): Promise<void> {
    await this.initTranslation();

    this.translater.onTranslationReady.subscribe((value: boolean) => {
      if (value) {
        this.reloadApp();
        this.isTranslationReady = true;
        this.setINavBarConfig();
      }
    });

    this.authService.isConnected.subscribe((response: boolean) => {
      this.isConnected = response || this.oAuthService.hasValidAccessToken();

      if (this.isConnected) {
        this.loadUser();
      }
    });
  }

  public logout(): void {
    this.oAuthService.logOut();
  }

  private loadUser(): void {
    this.authService.getMe().subscribe({
      next: () => {
        this.isUserReady = true;
        this.setINavBarConfig();
        this.loading = false;

        if (this.router.url.startsWith('/callback')) {
          this.router.navigate(['constatations']);
        }
      },
      error: () => {
        this.toasterService.push(
          'error',
          this.translater.translate('global.error'),
          this.translater.translate('auth.error.get-me')
        );
        this.loading = false;
      },
    });
  }

  private setINavBarConfig(): void {
    if (this.isTranslationReady && this.isUserReady) {
      const user = this.authService.connectedUser;

      // Structure adaptée à l'interface INavbarConf de i-essentials v20.
      this.config = {
        app: 'IValidation',
        home: user?.tenant || 'Inforius',
        user: {
          firstname: user?.firstName ?? '',
          lastname: user?.lastName ?? '',
          fullname: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
        },
        version: '1.0.0',
        langs: ['FR', 'NL', 'EN', 'DE'],
        links: [
          {
            icon: 'bi-table',
            label: this.translater.translate('list.title'),
            command: () => this.router.navigate(['constatations']),
          },
        ],
        options: [
          {
            icon: 'bi-box-arrow-right',
            label: this.translater.translate('navbar.logout'),
            command: () => this.logout(),
          },
        ],
        toAssistance: () => this.router.navigate(['assistance']),
      };
    }
  }

  // Le navbar v20 émet la langue en majuscules ('FR' | 'EN' | 'NL' | 'DE').
  public onLangChange(lang: 'FR' | 'EN' | 'NL' | 'DE'): void {
    this.updateLanguage(lang.toLowerCase() as Language);
  }

  private async updateLanguage(lang: Language): Promise<void> {
    this.loading = true;
    localStorage.setItem('lang', lang);

    await this.translater.setCurrentLang(lang);

    this.reloadApp();
  }

  private async initTranslation(): Promise<void> {
    this.loading = true;

    const lang: string | null = localStorage.getItem('lang');
    const allLanguages: Language[] = ['en', 'nl', 'de', 'fr'];

    if (!lang || lang === 'undefined' || lang === 'null') {
      const userLang: string = navigator.language
        .split('-')
        .reverse()[0]
        .toLowerCase();
      localStorage.setItem(
        'lang',
        allLanguages.indexOf(userLang as Language) >= 0 ? userLang : 'fr'
      );
    }

    await this.translater.init(
      localStorage.getItem('lang') as string,
      allLanguages,
      ['assets/translations', 'assets/i18n']
    );

    this.toasterService.setKey('iValidation');
    this.reloadApp();
  }

  private reloadApp(): void {
    this.loading = true;

    setTimeout(() => {
      this.loading = false;
    }, 0);
  }
}
